import { useEffect, useState } from 'react';

import { LOCAL_STORAGE } from '@/constants';
import { useIndexedDB } from '@/hooks/useIndexedDB';
import { useOrganization } from '@/hooks/useOrganization';
import { CachedAdditionalCategory } from '@/types/DataModel';

import { useAdditionalCategory } from '../queries';

const useCachedAdditionalCategory = (additionalCategoryCode?: string, additionalDataModelName?: string, extension?: string) => {

    const lookupLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);
    const { organization } = useOrganization();
    const [isCached, setIsCached] = useState(true);
    const [isLoading, setIsLoading] = useState(additionalCategoryCode ? true : false);
    const [cachedAdditionalCategory, setCachedAdditionalCategory] = useState<CachedAdditionalCategory | undefined>(undefined);

    const { data: additionalCategory } = useAdditionalCategory({
        variables: {
            dataModel: additionalDataModelName ?? '',
            extension: extension ?? '',
            leaf: true,
            query: additionalCategoryCode,
            limit: 1,
        },
        enabled: additionalCategoryCode !== undefined && !isCached
    });

    const {
        add: addAdditionalCategoryToCache,
        get: getAdditionalCategoryFromCache,
        deleteAllByIndex: deleteAdditionalCategoryFromCache
    } = useIndexedDB<CachedAdditionalCategory>('additionalCategories');

    useEffect(() => {

        const loadCachedAdditionalCategory = async () => {

            if (!organization || !additionalDataModelName || !additionalCategoryCode) {
                return;
            }

            const cacheKey = [additionalDataModelName, additionalCategoryCode, organization.dataModelHash, lookupLanguage || 'en'];
            const cachedAdditionalCategory = await getAdditionalCategoryFromCache(cacheKey);

            if (!cachedAdditionalCategory || cachedAdditionalCategory.dataModelHash !== organization.dataModelHash) {
                setIsCached(false);
            } else {
                setCachedAdditionalCategory(cachedAdditionalCategory);
                setIsLoading(false);
            }
        };

        loadCachedAdditionalCategory();

        //eslint-disable-next-line
    }, [additionalCategoryCode, lookupLanguage]);

    useEffect(() => {

        const cacheAdditionalCategory = async () => {

            if (!additionalCategory || !organization || !additionalDataModelName || !additionalCategoryCode) {
                return;
            }

            const cacheKey = [additionalDataModelName, additionalCategoryCode, organization.dataModelHash, lookupLanguage || 'en'];
            const cachedAdditionalCategory = {
                ...additionalCategory,
                dataModelHash: organization.dataModelHash,
                additionalModule: additionalDataModelName,
                additionalCategoryNameOrCode: additionalCategoryCode,
                translatedLanguage: lookupLanguage || 'en'
            };

            await deleteAdditionalCategoryFromCache('additionalCategoryNameOrCode', additionalCategoryCode);
            await addAdditionalCategoryToCache(cacheKey, cachedAdditionalCategory);

            setCachedAdditionalCategory(cachedAdditionalCategory);
            setIsLoading(false);
        };

        cacheAdditionalCategory();

        //eslint-disable-next-line
    }, [additionalCategory, lookupLanguage]);

    return {
        isLoading,
        cachedAdditionalCategory,
    };
};

export { useCachedAdditionalCategory };
